import { DatePicker, notification } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Button from '../../../../../../components/Button';
import { ButtonCancel } from '../../../../../../components/Button/styles';
import { formatarData } from '../../../../../../helpers/data';
import apiShipper from '../../../../../../services/apiShipper';
import { useAgendamentoAlteracaoContext } from '../../../../Contextos/AgendamentoAlteracaoContexto';
import StatusAlteracaoEnum from '../../../../Enum/StatusAlteracaoEnum';
import StatusLoteEnum from '../../../../Enum/StatusLoteEnum';
import TipoAlteracaoEnum from '../../../../Enum/TipoAlteracaoEnum';
import {
  AgendamentoContainer,
  AgendamentoInfo,
  CustomModal,
  FieldContainer,
} from '../styles';

const RetomarModal = ({
  visible,
  setVisible,
  agendamento,
  setAgendamento,
  actionPayload,
}) => {
  const [confirmLoading, setConfirmLoading] = useState({
    agora: false,
    agendar: false,
    cancelar: false,
  });
  const [dataAgendamento, setDataAgendamento] = useState();
  const { removeAgendamentoAlteracao } = useAgendamentoAlteracaoContext();
  const history = useHistory();

  const resetLoading = () => {
    setConfirmLoading({
      agora: false,
      agendar: false,
      cancelar: false,
    });
  };

  const handleRetomarAgora = async () => {
    setConfirmLoading({ agora: true, agendar: false, cancelar: false });

    try {
      const payload = {
        data_alteracao: moment().format(),
        status_lote: StatusLoteEnum.CARREGANDO,
        ...actionPayload,
      };

      await apiShipper.post('alteracao-demanda', payload);

      notification.success({
        message: 'Sucesso!',
        description: 'Lotes retomados com sucesso',
      });
      history.push({
        pathname: `/detalhesDemanda/${actionPayload.id_demanda}`,
      });
    } catch {
      notification.error({
        message: 'Ocorreu um erro inesperado ao retomar os lotes',
        description: 'Se o problema persistir, entre em contato com o suporte',
      });
    } finally {
      resetLoading();
      setVisible();
    }
  };

  const handleAgendar = async () => {
    setConfirmLoading({ agora: false, agendar: true, cancelar: false });

    try {
      const payload = {
        data_alteracao: moment(dataAgendamento)
          .utc(true)
          .format(),
        status_lote: StatusLoteEnum.CARREGANDO,
        tipo_alteracao: TipoAlteracaoEnum.RETOMAR,
        ...actionPayload,
      };

      await apiShipper.post('alteracao-demanda', payload);

      notification.success({
        message: 'Sucesso!',
        description: 'Agendamento de retomada de lotes realizado com sucesso',
      });

      setAgendamento({ data: dataAgendamento });
      history.push({
        pathname: `/detalhesDemanda/${actionPayload.id_demanda}`,
      });
    } catch {
      notification.error({
        message: 'Ocorreu um erro inesperado ao agendar a retomada dos lotes',
        description: 'Se o problema persistir, entre em contato com o suporte',
      });
    } finally {
      resetLoading();
      setVisible();
    }
  };

  const handleCancelAgendamento = async () => {
    setConfirmLoading({ agora: false, agendar: false, cancelar: true });

    try {
      await apiShipper.put('alteracao-demanda', {
        id_demanda: actionPayload.id_demanda,
        status: StatusAlteracaoEnum.status.CANCELADO,
        tipo_alteracao: TipoAlteracaoEnum.RETOMAR,
      });

      notification.success({
        message: 'Sucesso!',
        description: 'Agendamento de retomada cancelado com sucesso.',
      });

      removeAgendamentoAlteracao(agendamento);
      setAgendamento({ data: null });
      setDataAgendamento(null);
    } catch (error) {
      notification.error({
        message: 'Ocorreu um erro inesperado ao cancelar o agendamento',
        description: 'Se o problema persistir, entre em contato com o suporte',
      });
    } finally {
      resetLoading();
      setVisible();
    }
  };

  useEffect(() => {
    if (!visible) setDataAgendamento(null);
  }, [visible]);

  return (
    <CustomModal
      closable={false}
      title="Retomar todos os lotes"
      visible={visible}
      footer={[
        <Button key="fechar" type="secondary" onClick={() => setVisible()}>
          Fechar
        </Button>,
        <Button
          key="alterar"
          type="secondary"
          loading={confirmLoading.agora}
          onClick={handleRetomarAgora}
          disabled={dataAgendamento}
        >
          Retomar agora
        </Button>,
        <Button
          key="agendar"
          type="primary"
          loading={confirmLoading.agendar}
          onClick={handleAgendar}
          disabled={!dataAgendamento}
        >
          Agendar
        </Button>,
      ]}
    >
      <FieldContainer>
        <p>
          Você poderá suspender o carregamento novamente. Notificaremos a
          alteração para as transportadoras.
        </p>
      </FieldContainer>
      <FieldContainer>
        <p>Agendar alteração</p>
        <AgendamentoContainer>
          <DatePicker
            data-testid="data-agendamento"
            value={dataAgendamento}
            name="dataAgendamento"
            format="DD/MM/YYYY"
            disabled={agendamento.data}
            disabledDate={current => current.isBefore(moment())}
            style={{ width: '60%' }}
            onChange={data => setDataAgendamento(data)} // TODO: evitar multiplos render
          />
          <div style={{ width: '10px' }} />
          {agendamento.data && (
            <ButtonCancel
              loading={confirmLoading.cancelar}
              onClick={handleCancelAgendamento}
            >
              Cancelar
            </ButtonCancel>
          )}
        </AgendamentoContainer>
      </FieldContainer>
      {agendamento.data && (
        <AgendamentoInfo>
          <p>{`Retomada agendada para ${formatarData(agendamento.data)}`}</p>
        </AgendamentoInfo>
      )}
    </CustomModal>
  );
};

RetomarModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  agendamento: PropTypes.shape({
    data: PropTypes.string,
  }),
  setAgendamento: PropTypes.func.isRequired,
  actionPayload: PropTypes.shape({
    id_demanda: PropTypes.number,
    cliente_razao_social: PropTypes.string,
    origem: PropTypes.string,
    destino: PropTypes.string,
    email_filiais: PropTypes.arrayOf(PropTypes.string),
  }),
};

RetomarModal.defaultProps = {
  agendamento: null,
  actionPayload: null,
};

export default RetomarModal;
